import type { Intersection } from 'three';
import type { RootState } from '@react-three/fiber/dist/declarations/src/core/store';
import RaycasterClass from '@modules/three/common/raycast';
import type { ITool } from '@modules/three/tools/ITool';
import type { ThreeRootState } from '@modules/three/common/BaseThreeScene';
import type JawSegmentation from '@segmentation/three/JawSegmentation';
import { gumEditStore } from '@segmentation/store/gumEditStore';
import type { Vertex } from '@thirdPartyLibraries/three-mesh-halfedge/src';

export interface GumEditHost {
  getJaw(): JawSegmentation | null;
}

/** Snap radius for picking border vertices (world mm). */
const SNAP_RADIUS_MM = 4;
/** Pointer movement above this many pixels counts as an orbit drag, not a click. */
const CLICK_SLOP_PX = 5;

/**
 * Pointer tool for the gum-edit bridge step: hovering snaps a marker to the
 * nearest gum border vertex; a click (not an orbit drag) toggles it in the
 * two-vertex selection.
 */
export class GumEditTool implements ITool {
  readonly name = 'gum-edit';

  private downX = 0;
  private downY = 0;

  constructor(private readonly host: GumEditHost) {}

  onDeactivate(): void {
    this.host.getJaw()?.showGumEditHover(null);
  }

  onPointerDown(ev: PointerEvent): void {
    this.downX = ev.clientX;
    this.downY = ev.clientY;
  }

  onPointerMove(ev: PointerEvent, _hit: Intersection | null, three: RootState | ThreeRootState): void {
    const jaw = this.host.getJaw();
    if (!jaw) return;
    const hit = this.raycastJaw(ev, three);
    jaw.showGumEditHover(hit ? this.snapToBorder(jaw, hit) : null);
  }

  onPointerUp(ev: PointerEvent, _hit: Intersection | null, three: RootState | ThreeRootState): void {
    const jaw = this.host.getJaw();
    if (!jaw) return;
    const isClick = Math.hypot(ev.clientX - this.downX, ev.clientY - this.downY) <= CLICK_SLOP_PX;
    if (!isClick) return;
    const hit = this.raycastJaw(ev, three);
    const vertex = hit ? this.snapToBorder(jaw, hit) : null;
    if (vertex) {
      gumEditStore.getState().setSelectedCount(jaw.toggleGumEditSelection(vertex));
    }
  }

  private snapToBorder(jaw: JawSegmentation, hit: Intersection): Vertex | null {
    return jaw.findNearestBorderVertex(hit.point, SNAP_RADIUS_MM);
  }

  private raycastJaw(ev: PointerEvent, three: RootState | ThreeRootState): Intersection | null {
    const jaw = this.host.getJaw();
    if (!jaw) return null;
    return RaycasterClass.getFirstObjectIntersection(ev, jaw, three.camera, false);
  }
}
